import { ChangeEvent, KeyboardEvent, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { createComponentStyleHook } from "capitalrx-components-2.0/lib/tokens";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";
import { highlight, languages } from "prismjs";
import "prismjs/components/prism-css";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-json";
import "prismjs/components/prism-markup";
import "prismjs/components/prism-python";
import "prismjs/components/prism-sql";
import "prismjs/components/prism-typescript";

import { useId } from "../../hooks/useId";
import { debounce } from "../../utilities/debounce";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { Placeholder } from "../TextArea/Placeholder";
import { StyledNativeInput } from "../TextArea/StyledNativeInput";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { CodeAreaProps } from "./CodeArea";
import { EditorRoot } from "./EditorRoot";
import { SupportedLanguages } from "./SharedProps";

import textAreaStyles from "../TextArea/styles.module.scss";
import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

const useResolvedCodeAreaStyles = createComponentStyleHook<CodeAreaProps>({
  legacyStyles,
  tokenStyles,
  componentTokens: "code_area",
});

/**
 * The `Input` component properties.
 */
export interface InputProps extends CodeAreaProps {
  /**
   * Indicates whether the input is required.
   */
  readonly required?: boolean;
  /**
   * The error message to display.
   */
  error?: string | null;
}

interface Record {
  readonly value: string;
  readonly selectionStart: number;
  readonly selectionEnd: number;
}

interface History {
  readonly stack: ReadonlyArray<Record & { timestamp: number }>;
  readonly offset: number;
}

const KEYCODE_Y = "KeyY";
const KEYCODE_Z = "KeyZ";
const KEYCODE_M = "KeyM";
const KEY_PARENS = "(";
const KEYCODE_BRACKETS = "BracketLeft";
const KEYCODE_QUOTE = "Quote";
const KEYCODE_BACK_QUOTE = "Backquote";

const HISTORY_LIMIT = 100;
const HISTORY_TIME_GAP = 3000;

const isWindows = typeof window !== "undefined" && "navigator" in window && /Win/i.test(navigator.platform);
const isMacLike = typeof window !== "undefined" && "navigator" in window && /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);

function getLines(text: string, position?: number): Array<string> {
  return text.substring(0, position).split("\n");
}

function highlighter(text: string, language: SupportedLanguages): string {
  let grammar;
  switch (language) {
    case "javascript":
      grammar = languages.js;
      break;
    case "typescript":
      grammar = languages.ts;
      break;
    case "json":
      grammar = languages.json;
      break;
    case "css":
      grammar = languages.css;
      break;
    case "sql":
      grammar = languages.sql;
      break;
    case "python":
      grammar = languages.python;
      break;
    case "xml":
      grammar = languages.xml;
      break;
    case "yaml":
      grammar = languages.yaml;
      break;
    default:
      break;
  }

  if (!grammar) return text;
  return highlight(text, grammar, language);
}

export const Input = forwardRef<HTMLTextAreaElement, InputProps>(function Input(props, outerRef) {
  const {
    id,
    label,
    instructions,
    tooltip,
    hideInstructions,
    hideLabel,
    defaultValue = null,
    required = false,
    autoFocus,
    lines,
    placeholder,
    tabIndex,
    language,
    displayMode = "dark",
    scrollable = true,
    error,
    onChange,
    onFocusEnter,
    onFocusExit,
  } = props;

  // For now hardcode these, if we need these paramterized we can just move them up.
  const tabSize = 2;
  const insertSpaces = true;
  const ignoreTabKey = false;

  const { value = defaultValue } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedCodeAreaStyles(props);

  let displayLines: number | undefined = undefined;

  if (lines === undefined || typeof lines === "number") displayLines = lines as number | undefined;
  else if (lines === "auto") {
    displayLines = getLines(value ?? "").length;
  }

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      textAreaStyles.TextArea,
      "text-area",
      {
        "fixed-height": displayLines !== undefined,
      },
    ],
    testIdPrefix: "text-area",
  });
  const computedId = id ?? useId();
  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const isDarkMode = displayMode === "dark" && !(readOnly || submitting);
  const [invalid] = useValidity();
  const previewRef = useRef<HTMLPreElement | null>(null);

  const history = useRef<History>({ stack: [], offset: -1 });
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const [capture, setCapture] = useState(true);

  const inputRefHandler = useCallback(
    (ref: HTMLTextAreaElement | null) => {
      inputRef.current = ref;

      if (outerRef) {
        if (typeof outerRef === "function") {
          outerRef(ref);
        } else {
          outerRef.current = ref;
        }
      }
    },
    [outerRef],
  );

  useEffect(() => {
    if (outerRef) {
      if (typeof outerRef === "function") {
        outerRef(inputRef.current);
      } else {
        outerRef.current = inputRef.current;
      }
    }

    return () => {
      if (outerRef) {
        if (typeof outerRef === "function") {
          outerRef(null);
        } else {
          outerRef.current = null;
        }
      }
    };
  }, [outerRef]);

  const recordChange = useCallback((record: Record, overwrite = false): void => {
    const { stack, offset } = history.current;

    if (stack.length && offset > -1) {
      // When something updates, drop the redo operations
      history.current = {
        ...history.current,
        stack: stack.slice(0, offset + 1),
      };

      // Limit the number of operations to 100
      const count = history.current.stack.length;

      if (count > HISTORY_LIMIT) {
        const extras = count - HISTORY_LIMIT;

        history.current = {
          stack: stack.slice(extras, count),
          offset: Math.max(history.current.offset - extras, 0),
        };
      }
    }

    const timestamp = Date.now();

    if (overwrite) {
      const last = history.current.stack[history.current.offset];

      if (last && timestamp - last.timestamp < HISTORY_TIME_GAP) {
        // A previous entry exists and was in short interval

        // Match the last word in the line
        const re = /[^a-z0-9]([a-z0-9]+)$/i;

        // Get the previous line
        const previous = getLines(last.value, last.selectionStart).pop()?.match(re);

        // Get the current line
        const current = getLines(record.value, record.selectionStart).pop()?.match(re);

        if (previous?.[1] && current?.[1]?.startsWith(previous[1])) {
          const stack = [...history.current.stack];
          stack[history.current.offset] = { ...record, timestamp };

          history.current = {
            ...history.current,
            stack,
          };
          return;
        }
      }
    }

    history.current = {
      stack: [...history.current.stack, { ...record, timestamp }],
      offset: history.current.offset + 1,
    };
  }, []);

  const recordCurrentState = useCallback((): void => {
    const input = inputRef.current;

    if (!input) return;

    // Save current state of the input
    const { value, selectionStart, selectionEnd } = input;

    recordChange({
      value,
      selectionStart,
      selectionEnd,
    });
  }, [recordChange]);

  useEffect(() => {
    recordCurrentState();
  }, []);

  const updateInput = useCallback(
    (record: Record): void => {
      const input = inputRef.current;

      if (!input) return;

      // Update values and selection state
      input.value = record.value;
      input.selectionStart = record.selectionStart;
      input.selectionEnd = record.selectionEnd;

      onChange?.(record.value, value);
    },
    [value, onChange],
  );

  const applyEdits = useCallback(
    (record: Record): void => {
      // Save last selection state
      const input = inputRef.current;
      const last = history.current.stack[history.current.offset];

      if (last && input) {
        const stack = [...history.current.stack];
        stack[history.current.offset] = {
          ...last,
          selectionStart: input.selectionStart,
          selectionEnd: input.selectionEnd,
        };

        history.current = {
          ...history.current,
          stack,
        };
      }

      // Save the changes
      recordChange(record);
      updateInput(record);
    },
    [recordChange, updateInput],
  );

  const undoEdit = useCallback(() => {
    const { stack, offset } = history.current;

    // Get the previous edit
    const record = stack[offset - 1];

    if (record) {
      // Apply the changes and update the offset
      updateInput(record);
      history.current = {
        ...history.current,
        offset: Math.max(offset - 1, 0),
      };
    }
  }, [updateInput]);

  const redoEdit = useCallback(() => {
    const { stack, offset } = history.current;

    // Get the next edit
    const record = stack[offset + 1];

    if (record) {
      // Apply the changes and update the offset
      updateInput(record);

      history.current = {
        ...history.current,
        offset: Math.min(offset + 1, stack.length - 1),
      };
    }
  }, [updateInput]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === "Escape") {
        e.preventDefault();
        setCapture(false);
      }

      const { value, selectionStart, selectionEnd } = e.currentTarget;

      const tabCharacter = (insertSpaces ? " " : "\t").repeat(tabSize);

      if (e.key === "Tab" && !ignoreTabKey && capture) {
        // Prevent focus change
        e.preventDefault();

        if (e.shiftKey) {
          // Unindent selected lines
          const linesBeforeCaret = getLines(value, selectionStart);
          const startLine = linesBeforeCaret.length - 1;
          const endLine = getLines(value, selectionEnd).length - 1;
          const nextValue = value
            .split("\n")
            .map((line, i) => {
              if (i >= startLine && i <= endLine && line.startsWith(tabCharacter)) {
                return line.substring(tabCharacter.length);
              }

              return line;
            })
            .join("\n");

          if (value !== nextValue) {
            const startLineText = linesBeforeCaret[startLine];

            applyEdits({
              value: nextValue,
              // Move the start cursor if first line in selection was modified. It was modified only if it started with a tab
              selectionStart: startLineText?.startsWith(tabCharacter) ? selectionStart - tabCharacter.length : selectionStart,
              // Move the end cursor by total number of characters removed
              selectionEnd: selectionEnd - (value.length - nextValue.length),
            });
          }
        } else if (selectionStart !== selectionEnd) {
          // Indent selected lines
          const linesBeforeCaret = getLines(value, selectionStart);
          const startLine = linesBeforeCaret.length - 1;
          const endLine = getLines(value, selectionEnd).length - 1;
          const startLineText = linesBeforeCaret[startLine];

          applyEdits({
            value: value
              .split("\n")
              .map((line, i) => {
                if (i >= startLine && i <= endLine) {
                  return tabCharacter + line;
                }

                return line;
              })
              .join("\n"),
            // Move the start cursor by number of characters added in first line of selection. Don't move it if it there was no text before cursor
            selectionStart: startLineText && /\S/.test(startLineText) ? selectionStart + tabCharacter.length : selectionStart,
            // Move the end cursor by total number of characters added
            selectionEnd: selectionEnd + tabCharacter.length * (endLine - startLine + 1),
          });
        } else {
          const updatedSelection = selectionStart + tabCharacter.length;

          applyEdits({
            // Insert tab character at caret
            value: `${value.substring(0, selectionStart)}${tabCharacter}${value.substring(selectionEnd)}`,
            // Update caret position
            selectionStart: updatedSelection,
            selectionEnd: updatedSelection,
          });
        }
      } else if (e.key === "Backspace") {
        const hasSelection = selectionStart !== selectionEnd;
        const textBeforeCaret = value.substring(0, selectionStart);

        if (textBeforeCaret.endsWith(tabCharacter) && !hasSelection) {
          // Prevent default delete behaviour
          e.preventDefault();

          const updatedSelection = selectionStart - tabCharacter.length;

          applyEdits({
            // Remove tab character at caret
            value: `${value.substring(0, selectionStart - tabCharacter.length)}${value.substring(selectionEnd)}`,
            // Update caret position
            selectionStart: updatedSelection,
            selectionEnd: updatedSelection,
          });
        }
      } else if (e.key === "Enter") {
        // Ignore selections
        if (selectionStart === selectionEnd) {
          // Get the current line
          const line = getLines(value, selectionStart).pop();
          const matches = line?.match(/^\s+/);

          if (matches?.[0]) {
            e.preventDefault();

            // Preserve indentation on inserting a new line
            const indent = `\n${matches[0]}`;
            const updatedSelection = selectionStart + indent.length;

            applyEdits({
              // Insert indentation character at caret
              value: `${value.substring(0, selectionStart)}${indent}${value.substring(selectionEnd)}`,
              // Update caret position
              selectionStart: updatedSelection,
              selectionEnd: updatedSelection,
            });
          }
        }
      } else if (e.key === KEY_PARENS || e.code === KEYCODE_BRACKETS || e.code === KEYCODE_QUOTE || e.code === KEYCODE_BACK_QUOTE) {
        let chars;

        if (e.key === KEY_PARENS && e.shiftKey) {
          chars = ["(", ")"];
        } else if (e.code === KEYCODE_BRACKETS) {
          if (e.shiftKey) {
            chars = ["{", "}"];
          } else {
            chars = ["[", "]"];
          }
        } else if (e.code === KEYCODE_QUOTE) {
          if (e.shiftKey) {
            chars = ['"', '"'];
          } else {
            chars = ["'", "'"];
          }
        } else if (e.code === KEYCODE_BACK_QUOTE && !e.shiftKey) {
          chars = ["`", "`"];
        }

        // If text is selected, wrap them in the characters
        if (selectionStart !== selectionEnd && chars) {
          e.preventDefault();

          applyEdits({
            value: `${value.substring(0, selectionStart)}${chars[0]}${value.substring(selectionStart, selectionEnd)}${chars[1]}${value.substring(selectionEnd)}`,
            // Update caret position
            selectionStart,
            selectionEnd: selectionEnd + 2,
          });
        }
      } else if ((isMacLike ? e.metaKey && e.code === KEYCODE_Z : e.ctrlKey && e.code === KEYCODE_Z) && !e.shiftKey && !e.altKey) {
        e.preventDefault();

        undoEdit();
      } else if (
        (isMacLike ? e.metaKey && e.code === KEYCODE_Z && e.shiftKey : e.ctrlKey && e.code === KEYCODE_Z && e.shiftKey)
        && !e.altKey
      ) {
        e.preventDefault();

        redoEdit();
      } else if ((isWindows ? e.ctrlKey && e.code === KEYCODE_Y : e.ctrlKey && e.code === KEYCODE_Z && e.shiftKey) && !e.altKey) {
        e.preventDefault();

        redoEdit();
      } else if (e.code === KEYCODE_M && e.ctrlKey && (isMacLike ? e.shiftKey : true)) {
        e.preventDefault();

        // Toggle capturing tab key so users can focus away
        setCapture(false);
      }
    },
    [applyEdits, undoEdit, redoEdit, tabSize, insertSpaces, ignoreTabKey, capture],
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      const { value: newValue, selectionStart, selectionEnd } = e.currentTarget;

      recordChange({ value: newValue, selectionStart, selectionEnd }, true);
      onChange?.(newValue, value);
    },
    [value, onChange, recordChange],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
    setCapture(true);
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
    setCapture(false);
  }, [onFocusExit]);

  const highlighted = useMemo(() => highlighter(value ?? "", language), [value, language]);

  const scrollHandler = useCallback(
    debounce(() => {
      if (!previewRef.current) return;

      previewRef.current.style.transform = `translate(-${inputRef.current?.scrollLeft ?? 0}px, -${inputRef.current?.scrollTop ?? 0}px)`;
    }),
    [],
  );

  const describedByIds: string[] = [];
  if (instructions) describedByIds.push(`${computedId}-instructions`);
  if (error) describedByIds.push(`${computedId}-error`);
  const ariaDescribedBy = describedByIds.length ? describedByIds.join(" ") : undefined;

  return (
    <StyleModeBoundary mode={mode}>
      <LoadingGroup.Placeholder
        fallback={<Placeholder lines={displayLines} hasInstructions={!!instructions} />}
      >
        <InputContainer
          {...getBasePropsFromHtmlProps(baseHtmlProps)}
          id={computedId}
          label={label}
          instructions={instructions}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tooltip={tooltip}
          hideInstructions={hideInstructions}
          hideLabel={hideLabel}
        >
          <Body>
            <EditorRoot
              className={classNames(styles.CodeArea, {
                "read-only": readOnly || submitting,
                disabled,
                "no-scroll": scrollable === false,
                [styles.ReadOnly]: readOnly || submitting,
                [styles.Disabled]: disabled,
                [styles.NoScroll]: scrollable === false,
                [styles.DarkMode]: isDarkMode,
                [styles.LightMode]: !isDarkMode,
              })}
            >
              <div className={styles.ClientArea} aria-busy={submitting}>
                {!disabled && (
                  <div className={styles.PreviewBackground}>
                    <pre
                      className={styles.Preview}
                      ref={previewRef}
                      aria-hidden="true"
                      {...(typeof highlighted === "string"
                        ? { dangerouslySetInnerHTML: { __html: highlighted } }
                        : { children: highlighted })}
                    />
                  </div>
                )}
                <StyledNativeInput
                  aria-describedby={ariaDescribedBy}
                  aria-disabled={disabled}
                  aria-invalid={invalid}
                  aria-label={hideLabel && typeof label === "string" ? label : undefined}
                  aria-labelledby={label ? `${computedId}-label` : undefined}
                  aria-readonly={readOnly || submitting}
                  aria-required={disabled || readOnly ? undefined : required}
                  autoCapitalize="off"
                  autoComplete="off"
                  autoCorrect="off"
                  autoFocus={autoFocus}
                  className={classNames("input", styles.TextArea, { invalid, [styles.TextAreaEnabled]: !disabled })}
                  disabled={disabled}
                  id={computedId}
                  onBlur={blurHandler}
                  onChange={handleChange}
                  onFocus={focusHandler}
                  onKeyDown={handleKeyDown}
                  onScroll={scrollHandler}
                  placeholder={placeholder}
                  readOnly={readOnly || submitting}
                  ref={inputRefHandler}
                  rows={displayLines}
                  spellCheck={false}
                  tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
                  value={value ?? ""}
                />
              </div>
            </EditorRoot>
          </Body>
        </InputContainer>
      </LoadingGroup.Placeholder>
    </StyleModeBoundary>
  );
});
